import { FreshnessNote } from "@/components/FreshnessNote";
import { freshnessDate } from "@/lib/currentYear";
import { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "@/lib/router-compat";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHelmet } from "@/components/SEOHelmet";
import { PopularCardsSection } from "@/components/PopularCardsSection";
import { SegmentLinks } from "@/components/SegmentLinks";
import { CardSkeleton } from "@/components/CardSkeleton";
import { Button } from "@/components/ui/button";
import { useCards } from "@/hooks/useCards";
import { useOffers } from "@/hooks/useOffers";
import { useAdPlacements } from "@/hooks/useAdPlacements";
import { normalizeDbCard, toListCardVM } from "@/lib/cardCoreNormalizer";
import { getSiteOrigin } from "@/lib/ssr-safe";
import {
  hasNoAnnualFee,
  hasTravelInsurance,
  isPrepaid,
  isRealCreditCard,
  withAccount,
  withoutAccount,
  type CardRow,
} from "@/config/segmentPages";
import {
  ArrowLeft,
  Check,
  CreditCard as CreditCardIcon,
  Globe,
  Landmark,
  LifeBuoy,
  PiggyBank,
  Plane,
  RefreshCw,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  ThumbsUp,
  Timer,
  Wallet,
  HelpCircle,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";

const CardCard = lazy(() => import("@/components/CardCard").then((m) => ({ default: m.CardCard })));

export interface FinderAnswers {
  zweck: string;
  wichtig: string;
  konto: string;
  typ: string;
  schufa: string;
}

export const emptyAnswers: FinderAnswers = {
  zweck: "",
  wichtig: "",
  konto: "",
  typ: "",
  schufa: "",
};

interface Question {
  key: keyof FinderAnswers;
  question: string;
  help?: string;
  options: { value: string; label: string; description?: string; icon: LucideIcon }[];
}

export const finderQuestions: Question[] = [
  {
    key: "zweck",
    question: "Wofür brauchen Sie die Kreditkarte hauptsächlich?",
    options: [
      {
        value: "alltag",
        label: "Für den Alltag in Deutschland",
        description: "Einkaufen, Tanken, Restaurant",
        icon: Wallet,
      },
      {
        value: "reisen",
        label: "Für Reisen und Urlaub",
        description: "Mietwagen, Hotels, Zahlungen im Ausland",
        icon: Plane,
      },
      {
        value: "online",
        label: "Für Online-Einkäufe",
        description: "Shopping, Abos und Streaming",
        icon: ShoppingCart,
      },
      {
        value: "reserve",
        label: "Als Reserve für Notfälle",
        description: "Liegt meistens nur im Portemonnaie",
        icon: LifeBuoy,
      },
    ],
  },
  {
    key: "wichtig",
    question: "Was ist Ihnen dabei am wichtigsten?",
    options: [
      {
        value: "kostenlos",
        label: "Kostenlos",
        description: "Keine Jahresgebühr",
        icon: PiggyBank,
      },
      {
        value: "reisen",
        label: "Reisen",
        description: "Ausland, Urlaub und Mietwagen",
        icon: Plane,
      },
      {
        value: "mietwagen",
        label: "Mietwagen & Kaution",
        description: "Echte Kreditkarte mit Mietwagen-Vorteilen",
        icon: Sparkles,
      },
      {
        value: "versicherungen",
        label: "Versicherungen",
        description: "Reise- und Einkaufsschutz inklusive",
        icon: ShieldCheck,
      },
      {
        value: "echte-kreditkarte",
        label: "Echte Kreditkarte",
        description: "Mit Kreditrahmen und Zahlungsziel",
        icon: CreditCardIcon,
      },
      {
        value: "kein-girokonto",
        label: "Kein neues Girokonto",
        description: "Nur die Karte, kein Kontowechsel",
        icon: Wallet,
      },
    ],
  },
  {
    key: "konto",
    question: "Soll ein neues Girokonto dazugehören?",
    help: "Manche Karten gibt es nur im Paket mit einem Konto beim selben Anbieter.",
    options: [
      {
        value: "nein",
        label: "Nein, ich behalte mein Konto",
        description: "Nur die Karte, kein Kontowechsel",
        icon: CreditCardIcon,
      },
      {
        value: "ja",
        label: "Ja, ein neues Konto ist willkommen",
        description: "Karte und Konto aus einer Hand",
        icon: Landmark,
      },
      { value: "egal", label: "Das ist mir egal", description: "Beides kommt infrage", icon: Globe },
    ],
  },
  {
    key: "typ",
    question: "Brauchen Sie eine echte Kreditkarte mit Zahlungsziel?",
    help: "Für Mietwagen und Hotelkautionen wird fast immer eine echte Kreditkarte verlangt.",
    options: [
      {
        value: "kredit",
        label: "Ja, echte Kreditkarte mit Kreditrahmen",
        description: "Wird bei Mietwagen und Kautionen akzeptiert",
        icon: CreditCardIcon,
      },
      {
        value: "guthaben",
        label: "Nein, Guthabenbasis reicht mir",
        description: "Nur ausgeben, was aufgeladen ist",
        icon: Wallet,
      },
      {
        value: "egal",
        label: "Ich bin offen für beides",
        description: "Hauptsache, die Karte passt",
        icon: Globe,
      },
    ],
  },
  {
    key: "schufa",
    question: "Wie ist Ihre SCHUFA-Situation?",
    options: [
      { value: "ok", label: "Unproblematisch", description: "Keine negativen Einträge", icon: Check },
      {
        value: "unsicher",
        label: "Ich bin mir nicht sicher",
        description: "Lieber auf Nummer sicher gehen",
        icon: HelpCircle,
      },
      {
        value: "negativ",
        label: "Es gibt negative Einträge",
        description: "Karten ohne Bonitätsprüfung bevorzugt",
        icon: AlertTriangle,
      },
    ],
  },
];

interface Scored {
  card: CardRow;
  score: number;
  reasons: string[];
}

function scoreCard(card: CardRow, a: FinderAnswers): Scored {
  let score = 0;
  const reasons: string[] = [];
  const free = hasNoAnnualFee(card);
  const travel = hasTravelInsurance(card);
  const prepaid = isPrepaid(card);
  const realCredit = isRealCreditCard(card);

  if (a.zweck === "reisen" && travel) {
    score += 3;
    reasons.push("Reiseversicherung ist enthalten");
  }
  if (a.zweck === "reisen" && realCredit) score += 1;
  if (a.zweck === "alltag" && free) score += 2;
  if (a.zweck === "online" && free) score += 1;
  if (a.zweck === "online" && prepaid) {
    score += 2;
    reasons.push("Guthabenbasis begrenzt das Risiko beim Online-Einkauf");
  }
  if (a.zweck === "reserve" && realCredit) {
    score += 2;
    reasons.push("Echter Kreditrahmen für unvorhergesehene Ausgaben");
  }

  if (a.wichtig === "kostenlos" && free) {
    score += 3;
    reasons.push("Keine Jahresgebühr");
  }
  if (a.wichtig === "reisen" && travel) {
    score += 3;
    if (!reasons.includes("Reiseversicherung ist enthalten")) {
      reasons.push("Reiseversicherung ist enthalten");
    }
  }
  if (a.wichtig === "mietwagen" && realCredit && (card as any).rental_car_insurance) {
    score += 3;
    reasons.push("Echte Kreditkarte mit Mietwagen-Vorteilen");
  }
  if (a.wichtig === "versicherungen" && travel) {
    score += 3;
    if (!reasons.includes("Reiseversicherung ist enthalten")) {
      reasons.push("Reiseversicherung ist enthalten");
    }
  }
  if (a.wichtig === "echte-kreditkarte") {
    if (realCredit) {
      score += 3;
      reasons.push("Echte Kreditkarte mit Kreditrahmen");
    } else {
      score -= 4;
    }
  }
  if (a.wichtig === "kein-girokonto") {
    if (withoutAccount(card)) {
      score += 3;
      reasons.push("Kein neues Girokonto nötig");
    } else {
      score -= 4;
    }
  }

  if (a.konto === "nein") {
    if (withoutAccount(card)) {
      score += 3;
      reasons.push("Kein neues Girokonto nötig");
    } else {
      score -= 4;
    }
  }
  if (a.konto === "ja" && withAccount(card)) {
    score += 2;
    reasons.push("Girokonto ist inklusive");
  }

  if (a.typ === "kredit") {
    if (realCredit) score += 3;
    else score -= 5;
  }
  if (a.typ === "guthaben" && prepaid) {
    score += 3;
    reasons.push("Karte auf Guthabenbasis");
  }

  if (a.schufa === "negativ") {
    if (prepaid) {
      score += 4;
      if (!reasons.includes("Ohne Bonitätsprüfung erhältlich")) {
        reasons.push("Ohne Bonitätsprüfung erhältlich");
      }
    } else {
      score -= 5;
    }
  }
  if (a.schufa === "unsicher" && prepaid) score += 2;

  // Slight preference for cards without an annual fee when nothing else separates them
  if (free) score += 0.5;
  score += Math.min((card.boost_priority || 0) / 100, 0.4);

  if (reasons.length === 0) {
    if (free) reasons.push("Keine Jahresgebühr");
    if (travel) reasons.push("Reiseversicherung ist enthalten");
    if (withoutAccount(card)) reasons.push("Kein neues Girokonto nötig");
  }

  return { card, score, reasons: reasons.slice(0, 3) };
}

interface CardFinderProps {
  answers: FinderAnswers;
  onAnswer: (key: keyof FinderAnswers, value: string) => void;
  onReset: () => void;
}

export default function CardFinder({ answers, onAnswer, onReset }: CardFinderProps) {
  const navigate = useNavigate();
  const { data: cards = [], isLoading } = useCards();
  const { data: offers = [] } = useOffers();
  const { data: adPlacements = [] } = useAdPlacements();
  const [showMore, setShowMore] = useState(false);

  const domain = getSiteOrigin();
  const pageUrl = `${domain}/tools/kreditkarten-finder`;

  const answered = finderQuestions.filter((q) => answers[q.key]).length;
  const complete = answered === finderQuestions.length;

  const firstUnanswered = finderQuestions.findIndex((q) => !answers[q.key]);
  const [step, setStep] = useState(firstUnanswered === -1 ? finderQuestions.length - 1 : firstUnanswered);

  // Keep the visible step inside a sensible range when answers are reset externally
  useEffect(() => {
    if (answered === 0) setStep(0);
  }, [answered]);

  const offersMap = useMemo(() => {
    const map = new Map<string, any>();
    offers.forEach((offer: any) => {
      if (offer.card_id) map.set(offer.card_id, offer);
    });
    return map;
  }, [offers]);

  const sponsoredCardIds = useMemo(
    () => new Set(adPlacements.map((ad: any) => ad.card_id)),
    [adPlacements],
  );

  const sponsoredLabels = useMemo(() => {
    const map = new Map<string, string>();
    adPlacements.forEach((ad: any) => {
      if (ad.card_id) map.set(ad.card_id, ad.label || "Anzeige");
    });
    return map;
  }, [adPlacements]);

  const results = useMemo(() => {
    if (!complete) return [];
    return (cards as CardRow[])
      .map((card) => scoreCard(card, answers))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [cards, answers, complete]);

  // How many cards still fit while the questionnaire is in progress
  const liveMatches = useMemo(() => {
    if (answered === 0) return cards.length;
    return (cards as CardRow[]).filter((card) => scoreCard(card, answers).score > 0).length;
  }, [cards, answers, answered]);

  const tiles = useMemo(
    () =>
      results.map((entry) => {
        const card = entry.card as any;
        const normalized = { ...card, ...normalizeDbCard(card), id: String(card.id) };
        return {
          entry,
          card,
          tile: toListCardVM(normalized, offersMap.get(card.id)),
        };
      }),
    [results, offersMap],
  );

  const handleNavigate = useCallback(
    (page: string) => {
      if (page === "home") navigate("/");
    },
    [navigate],
  );

  const handleViewDetails = useCallback(
    (slug: string) => navigate(`/kreditkarten/${slug}`),
    [navigate],
  );

  const handleSelect = useCallback(
    (key: keyof FinderAnswers, value: string, index: number) => {
      onAnswer(key, value);
      if (answers[key] !== value && index < finderQuestions.length - 1) {
        setStep(index + 1);
      }
    },
    [answers, onAnswer],
  );

  const handleReset = useCallback(() => {
    onReset();
    setStep(0);
    setShowMore(false);
  }, [onReset]);

  const schemas = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Startseite", item: domain },
          { "@type": "ListItem", position: 2, name: "Kreditkarten-Finder", item: pageUrl },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Kreditkarten-Finder",
        applicationCategory: "FinanceApplication",
        operatingSystem: "Web",
        url: pageUrl,
        offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      },
    ],
    [domain, pageUrl],
  );

  const activeQuestion = finderQuestions[Math.min(step, finderQuestions.length - 1)];
  const activeIndex = finderQuestions.indexOf(activeQuestion);
  const progress = Math.round((answered / finderQuestions.length) * 100);

  const visibleTiles = showMore ? tiles : tiles.slice(0, 3);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHelmet
        title="Kreditkarten-Finder – in 5 Fragen zur passenden Karte"
        description="Beantworten Sie fünf kurze Fragen und erhalten Sie die Kreditkarten, die zu Ihrem Nutzungsverhalten passen — mit verständlicher Begründung."
        url={pageUrl}
        type="website"
        modifiedTime={freshnessDate()}
        additionalSchemas={schemas}
      />

      <Header onNavigate={handleNavigate} currentPage="tool-kreditkarten-finder" />

      <main className="flex-1">
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Kostenlos & unverbindlich
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">Kreditkarten-Finder</h1>
          <FreshnessNote className="mb-3" label="Kartendaten geprüft" />
          <p className="text-muted-foreground text-lg">
            Fünf kurze Fragen, rund 20 Sekunden — danach sehen Sie die Karten, die am besten zu Ihrer
            Situation passen, jeweils mit einer kurzen Begründung.
          </p>
        </section>

        {/* Questionnaire */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            {/* Progress */}
            <div className="px-5 sm:px-7 pt-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-medium text-foreground">
                  Frage {activeIndex + 1} von {finderQuestions.length}
                </span>
                <span className="text-muted-foreground" aria-live="polite">
                  {answered} von {finderQuestions.length} beantwortet
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {finderQuestions.map((q, index) => {
                  const done = !!answers[q.key];
                  const current = index === activeIndex;
                  const reachable = done || index <= answered;
                  return (
                    <button
                      key={q.key}
                      type="button"
                      disabled={!reachable}
                      onClick={() => setStep(index)}
                      aria-current={current ? "step" : undefined}
                      aria-label={`Zu Frage ${index + 1}: ${q.question}`}
                      className={`h-8 w-8 rounded-full text-xs font-semibold border transition-colors flex items-center justify-center ${
                        current
                          ? "border-primary bg-primary text-primary-foreground"
                          : done
                            ? "border-primary/40 bg-primary/10 text-primary"
                            : "border-border bg-muted text-muted-foreground"
                      } ${reachable ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                    >
                      {done && !current ? <Check className="w-4 h-4" /> : index + 1}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Question */}
            <fieldset className="px-5 sm:px-7 py-6">
              <legend className="sr-only">{activeQuestion.question}</legend>
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-1">
                {activeQuestion.question}
              </h2>
              {activeQuestion.help && (
                <p className="text-sm text-muted-foreground mb-4">{activeQuestion.help}</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {activeQuestion.options.map((option) => {
                  const active = answers[activeQuestion.key] === option.value;
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => handleSelect(activeQuestion.key, option.value, activeIndex)}
                      className={`text-left rounded-xl border p-4 transition-all duration-200 flex items-start gap-3 hover:-translate-y-0.5 hover:shadow-md ${
                        active
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-border bg-card hover:border-primary/50"
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                          active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium text-foreground leading-snug">
                          {option.label}
                        </span>
                        {option.description && (
                          <span className="block text-sm text-muted-foreground mt-0.5">
                            {option.description}
                          </span>
                        )}
                      </span>
                      {active && <Check className="w-4 h-4 text-primary flex-shrink-0 mt-1" />}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-3 mt-6">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(Math.max(0, activeIndex - 1))}
                  disabled={activeIndex === 0}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zurück
                </Button>
                <p className="text-sm text-muted-foreground" aria-live="polite">
                  {isLoading
                    ? "Karten werden geladen …"
                    : `${liveMatches} Karten passen aktuell zu Ihren Angaben`}
                </p>
              </div>
            </fieldset>
          </div>

          {answered > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {finderQuestions.map((q, index) => {
                const value = answers[q.key];
                if (!value) return null;
                const option = q.options.find((o) => o.value === value);
                return (
                  <button
                    key={q.key}
                    type="button"
                    onClick={() => setStep(index)}
                    className="rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-foreground hover:border-primary/50 transition-colors"
                  >
                    {option?.label ?? value}
                  </button>
                );
              })}
              <Button variant="ghost" size="sm" onClick={handleReset} className="gap-2">
                <RefreshCw className="w-3.5 h-3.5" />
                Neu starten
              </Button>
            </div>
          )}
        </section>

        {/* Results */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-2xl font-bold text-foreground mb-4">Ihr Ergebnis</h2>

          {!complete ? (
            <p className="text-muted-foreground">
              Beantworten Sie alle fünf Fragen, um Ihre Kartenempfehlungen zu sehen.
            </p>
          ) : isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : tiles.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-6">
              <p className="text-muted-foreground mb-2">
                Zu dieser Kombination passt aktuell keine Karte in unserem Vergleich.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Am häufigsten hilft es, die Angabe zur Kartenart oder zum Girokonto auf „egal" zu
                setzen.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => navigate("/kreditkarten")}>Alle Kreditkarten ansehen</Button>
                <Button variant="outline" onClick={handleReset}>
                  Antworten ändern
                </Button>
              </div>
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <CardSkeleton key={i} />
                  ))}
                </div>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleTiles.map(({ entry, card, tile }, index) => (
                  <div key={card.id} className="flex flex-col">
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                          index === 0
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {index === 0 ? "Beste Übereinstimmung" : `Platz ${index + 1}`}
                      </span>
                    </div>
                    {entry.reasons.length > 0 && (
                      <p className="text-sm text-muted-foreground mb-3">
                        <span className="text-foreground font-medium">Warum diese Karte: </span>
                        {entry.reasons.join(", ")}
                      </p>
                    )}
                    <div className="flex-1">
                      <CardCard
                        tile={tile}
                        rawCard={card}
                        offer={offersMap.get(card.id)}
                        isSponsored={sponsoredCardIds.has(card.id)}
                        sponsoredLabel={sponsoredLabels.get(card.id)}
                        priority={index < 3}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {!showMore && tiles.length > 3 && (
                <div className="flex justify-center mt-6">
                  <Button variant="outline" onClick={() => setShowMore(true)}>
                    Weitere passende Karten anzeigen ({tiles.length - 3})
                  </Button>
                </div>
              )}
            </Suspense>
          )}

          <p className="text-sm text-muted-foreground mt-8">
            Dazu passend: der{" "}
            <Link to="/tools/teilzahlungsrechner" className="text-primary underline">
              Teilzahlungsrechner
            </Link>{" "}
            zeigt, was die Rückzahlung in Raten kostet, und der{" "}
            <Link to="/tools/kostenrechner" className="text-primary underline">
              Kostenrechner
            </Link>{" "}
            vergleicht die jährlichen Kosten aller Karten.
          </p>

          <p className="text-xs text-muted-foreground mt-4">
            Die Empfehlung basiert auf den Angaben der Anbieter zu Jahresgebühr, Kartenart,
            Kontopflicht und Reiseversicherung. Sie ist keine Finanzberatung — prüfen Sie vor dem
            Abschluss immer das Preis- und Leistungsverzeichnis des Anbieters.
          </p>
        </section>

        <SegmentLinks />

        {/* Gängige Kreditkarten – always shown directly above the footer */}
        <PopularCardsSection
          currentPage="tool-kreditkarten-finder"
          forceVisible
          onViewDetails={handleViewDetails}
          onViewAllCards={() => navigate("/kreditkarten")}
        />
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
}
